import { Component, OnInit, ViewChild, Inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { map, tap } from 'rxjs/operators';
import {
  EntityServiceClient,
  LevelDto,
  LevelFilterDto,
  LevelServiceClient,
  PredictionModelFilterDto,
  RawMaterialFilterDto,
  RawMaterialService,
  ReferenceFilterDto
} from '@sharedrest/data.restservice';
import { NeqSelectAutocompleteComponent } from '@sharedcommon/components/select-autocomplete/neq-select-autocomplete.component';
import { UserService } from '@sharedcommon/services/user.service';
import { Constants } from '@sharedcommon/util/constants.util';
import { FunctionUtils } from '@sharedcommon/util/functions.util';
import { ToastService } from '@sharedcommon/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import {TreeNode} from 'primeng/api';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import { lastValueFrom } from 'rxjs';

@Component({
  templateUrl: 'advanced-rawmaterials-search-popin.component.html'
})
export class NeqAdvancedRawmaterialsSearchPopinComponent implements OnInit {

  @ViewChild('predictionModelSelectAutocomplete') predictionModelSelectAutocomplete: NeqSelectAutocompleteComponent;
  @ViewChild('referenceSelectAutocomplete') referenceSelectAutocomplete: NeqSelectAutocompleteComponent;
  @ViewChild('rawMaterialSelectAutocomplete') rawMaterialSelectAutocomplete: NeqSelectAutocompleteComponent;

  form = new FormGroup({
    level: new FormControl(),
    predictionModel: new FormControl(),
    reference: new FormControl(),
    rawMaterial: new FormControl()
  });
  levels: LevelDto[] = [];
  levelsTree: TreeNode[] = [];
  predictionModelsNumber = 0;
  referencesNumber = 0;
  rawMaterialsNumber = 0;
  onlyRawMaterial = false;
  selectedLevel: TreeNode;

  constructor(
    private readonly userService: UserService,
    private readonly levelService: LevelServiceClient,
    private readonly entityService: EntityServiceClient,
    private readonly rawMatService: RawMaterialService,
    private readonly toastService: ToastService,
    private readonly translate: TranslateService,
    private readonly dialogRef: MatDialogRef<NeqAdvancedRawmaterialsSearchPopinComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {onlyRawMaterial: boolean}
  ) {
  }

  ngOnInit() {
    if (this.data?.onlyRawMaterial) {
      this.onlyRawMaterial = true;
    }

    this.form.get('level').valueChanges.subscribe(() => this.onLevelSelected());
    this.form.get('predictionModel').valueChanges.subscribe(() => this.onPredictionModelChange());
    this.form.get('reference').valueChanges.subscribe(() => this.onReferenceChange());
    this.form.get('rawMaterial').valueChanges.subscribe(() => this.onRawMaterialChange());

    const filter = new LevelFilterDto();
    filter.page = Constants.ALL_PAGE;
    filter.withChildren = true;
    filter.depth = 2;
    filter.minDepth = 2;
    this.levelService.fetch(filter).subscribe(levels => {
      this.levels = levels.data;
      this.levelsTree = levels.data;
    } );
  }

  onLevelSelected() {
    this.predictionModelSelectAutocomplete.reset();
  }

  onPredictionModelChange() {
    this.referenceSelectAutocomplete.reset();
  }

  onReferenceChange() {
    this.rawMaterialSelectAutocomplete.reset();
  }

  onRawMaterialChange() {
    const selectedRawMaterial = this.form.get('rawMaterial').value;
    if (selectedRawMaterial) {
      // update form value else form.value has rawMaterial null even if form.get('rawMaterial').value has the right value
      this.form.updateValueAndValidity();
      this.dialogRef.close(this.form.value);
    }
  }


  filterLevels = (item: LevelDto, filter: string) => {
    return item && item.label.toLowerCase().indexOf(filter.toLowerCase()) >= 0;
  };

  predictionModelsDatasource = (searchTerm, page) => {
    const filter = new PredictionModelFilterDto();

    if (searchTerm) {
      filter.matchAny = true;
      filter.code = searchTerm;
      filter.label = searchTerm;
    }

    const selectedLevel = this.form.get('level').value;
    if (selectedLevel) {
      filter.inLevelIds = [selectedLevel.id];
    }

    filter.page = page;
    filter.count = 10;
    filter.sort = [];

    const restResponse = this.entityService.predictionModelsFetch(this.userService.getEntity(), filter);

    return lastValueFrom(
      restResponse.pipe(
          tap(result => this.predictionModelsNumber = result.total),
          map(result => result.data.map(predictionModel => {
            return {
              label: `${predictionModel.code} - ${predictionModel.label} (${predictionModel.description})`,
              value: predictionModel
            };
          }))
        ));
  };


  referencesDatasource = (searchTerm: string, page) => {
    const filter = new ReferenceFilterDto();

    if (searchTerm) {
      filter.matchAny = true;
      filter.code = searchTerm;
      filter.label = searchTerm;
    }

    const selectedPredictionModel = this.form.get('predictionModel').value;
    if (selectedPredictionModel) {
      filter.inPredictionModelIds = [selectedPredictionModel.value.id];
    } else {
      const selectedLevel = this.form.get('level').value;
      if (selectedLevel) {
        filter.inLevelIds = [selectedLevel.id];
      }
    }

    filter.page = page;
    filter.count = 10;
    filter.sort = [];

    const restResponse = this.entityService.referencesFetch(this.userService.getEntity(), filter);

    return lastValueFrom(
        restResponse.pipe(
          tap(result => this.referencesNumber = result.total),
          map(result => result.data.map(reference => {
            return {
              label: `${reference.code} - ${reference.label} (${reference.description})`,
              value: reference
            };
          }))
        ));
  };

  rawMaterialsDatasource = (searchTerm, page) => {
    const filter = new RawMaterialFilterDto();

    if (searchTerm) {
      filter.matchAny = true;
      filter.code = searchTerm;
      filter.label = searchTerm;
    }

    filter.archive = false;

    const selectedReference = this.form.get('reference').value;
    if (selectedReference) {
      filter.referenceId = selectedReference.value.id;
    } else {
      const selectedPredictionModel = this.form.get('predictionModel').value;
      if (selectedPredictionModel) {
        filter.predictionModelId = selectedPredictionModel.value.id;
      } else {
        const selectedLevel = this.form.get('level').value;
        if (selectedLevel) {
          filter.levelId = selectedLevel.id;
        }
      }
    }

    filter.page = page;
    filter.count = 10;
    filter.sort = [];

    const restResponse = this.rawMatService.fetch(this.userService.getEntity(), filter);

    return lastValueFrom(
      restResponse.pipe(
        tap(result => this.rawMaterialsNumber = result.total),
        map(result => result.data.map(rawMaterial => {
          return {
            label: `${rawMaterial.code} - ${rawMaterial.label}`,
            value: rawMaterial
          };
        }))
      ));
  };



  onFormSubmit() {
    if (this.onlyRawMaterial && FunctionUtils.isNotDefined(this.form.get('rawMaterial').value)) {
      this.toastService.toast(this.translate.instant('error.user.rawmaterials.advanced-search.invalid'));
      return;
    }
    this.dialogRef.close(this.form.value);
  }

  onCancel() {
    this.dialogRef.close(false);
  }

  nodeSelect() {
    if (this.selectedLevel && this.selectedLevel.children === null) {
      const level = {...this.selectedLevel};
      level.parent = null;
      level.children = null;
      this.form.get('level').patchValue(level);
      this.form.get('level').updateValueAndValidity();
    } else {
      this.selectedLevel = null;
    }
  }

}
